<template>
  <div
    ref="root"
    class="collection-player__zoom-container flex flex-auto pa-4 box-content justify-center"
    :class="{
      'overflow-hidden': pitcherSettings?.use_collection_player_with_annotations,
    }"
    :data-slide-id="item.id"
    @pointerdown="allowTouchMove(true)"
    @pointerup="allowTouchMove(false)"
    @touchend="allowTouchMove(false)"
    @touchstart="allowTouchMove(true)"
  >
    <MainImage
      v-if="isImage(item) || isSlide(item)"
      :fit-to-container-width="fitToWidth"
      :isActive="isActiveSlide"
      :item="item"
      :zoom="zoom"
    />

    <CVideo
      v-else-if="isVideo(item)"
      class="w-full h-full max-h-full max-w-full flex"
      :current-time="videoStartTime"
      ignore-aspect-ratio
      :options="{
        sources: item.file.content_url
          ? [
              {
                src: item.file.content_url,
              },
            ]
          : undefined,
      }"
      prevent-pointer-events
    />

    <iframe
      v-else-if="isWeb(item)"
      class="w-full h-full max-h-full max-w-full border-0"
      :src="item.file.content_url ?? undefined"
      :title="t('canvasUI.collectionPlayer.webSlide')"
    />

    <div v-else-if="!item.file || !item.file.content_type" class="flex items-center justify-center h-full">
      <CSpin :color="themeVars.primary" size="small" />
    </div>

    <div v-else>{{ t('canvasUI.collectionPlayer.unsupportedType') }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed, type ComputedRef, inject, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import CVideo from '@lib/components/CVideo/CVideo.vue'
import CSpin from '@lib/components/CSpin/CSpin.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { isImage, isSlide, isVideo, isWeb } from '@collection-player/utils/slide-type.util'
import MainImage from '@collection-player/components/MainImage.vue'
import { useAppStore } from '@collection-player/stores/app.store'
import type { CollectionSlide } from '@collection-player/types/collection-player.type'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'

const props = defineProps<{
  item: CollectionSlide
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

const {
  data,
  activeSlide,
  zoom: { zoom },
  fitToWidth,
  options,
  cacheNavigation,
  allowTouchMove,
} = useAppStore()

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const root = ref<HTMLElement | null>(null)
const isActiveSlide = computed(() => activeSlide.value?.id === props.item.id)
const cacheItemId = cacheNavigation.createCacheId(props.item.id)

const containsOnlyOneSlideAndOneGroup = computed(
  () => data.value.groups.length === 1 && data.value.groups[0].slides.length === 1,
)

const getVideoStartTime = () => {
  if (containsOnlyOneSlideAndOneGroup.value) return options?.videoStartTime

  const videoStartTime = cacheNavigation.get(cacheItemId)

  if (!videoStartTime) return undefined

  return parseFloat(videoStartTime)
}

const preserveVideoTime = () => {
  const video = root.value?.querySelector('video')

  if (!video) return

  cacheNavigation.set(cacheItemId, video.currentTime.toString())
}

onBeforeUnmount(() => {
  preserveVideoTime()
})

const videoStartTime = ref(getVideoStartTime())
</script>
